import { AxiosError } from "axios";
import { useGrowl } from "PFApp/use_growl";
import Checkbox from "PFComponents/checkbox/checkbox";
import ColourPicker from "PFComponents/colour_picker/colour_picker";
import ConfirmModal from "PFComponents/confirm/confirm_modal";
import { Select } from "PFComponents/select/select";
import { InputFieldSet } from "PFComponents/text/input_field_set";
import Toggle from "PFComponents/toggle/toggle";
import TooltipIcon from "PFComponents/tooltip_icon/tooltip_icon";
import { Typography } from "PFComponents/typography";
import { useResponseErrors } from "PFCore/helpers/use_response_errors";
import InfoIcon from "PFIcons/info.svg";
import colors from "PFTheme/tokens/colors";
import { BookingCategory } from "PFTypes";
import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { createBookingCategory, updateBookingCategory } from "../../services/api";
import css from "./booking_category_modal.module.scss";

type EditBookingCategoryModalProps = {
  category: BookingCategory | null;
  onClose: () => void;
  onSaved: () => void;
};

export const BookingCategoryModal = ({
  category,
  onClose,
  onSaved
}: EditBookingCategoryModalProps): JSX.Element => {
  const [color, setColor] = useState(category?.color || colors.paletteBlack0);
  const [key, setKey] = useState(category?.key);
  const [displayAs, setDisplayAs] = useState(category?.display_as);
  const [billable, setBillable] = useState(category?.billable ?? false);
  const [affectsAvailability, setAffectsAvailability] = useState(category?.affects_availability ?? false);
  const [archived, setArchived] = useState(category?.archived);
  const [main, setMain] = useState(category?.main || false);
  const [demandRelated, setDemandRelated] = useState(category?.activity_assignable ?? false);
  const [hidden, setHidden] = useState(category?.hidden || false);
  const [requiresApproval, setRequiresApproval] = useState(category?.requires_approval || "never");
  const [errors, setErrors] = useState<any>({});

  const isNew = !category?.id;
  const isSystemDefined = category?.system_defined;

  const { t } = useTranslation(["bookingModule", "translation"]);

  const growl = useGrowl();
  const { getFormattedErrors } = useResponseErrors();

  const requiresApprovalOptions = useMemo(
    () => [
      {
        id: "clashes",
        item: "clashes",
        displayElement: t("bookingModule:config.categories.requiresApprovalOptions.overbooking")
      },
      {
        id: "always",
        item: "always",
        displayElement: t("bookingModule:config.categories.requiresApprovalOptions.always")
      },
      {
        id: "never",
        item: "never",
        displayElement: t("bookingModule:config.categories.requiresApprovalOptions.never")
      }
    ],
    [t]
  );

  const requiresApprovalIndex = useMemo(
    () => requiresApprovalOptions.findIndex((option) => option.id === requiresApproval),
    [requiresApprovalOptions, requiresApproval]
  );

  const requiresApprovalValue = useMemo(
    () => requiresApprovalOptions.find((option) => option.id === requiresApproval)?.displayElement,
    [requiresApprovalOptions, requiresApproval]
  );

  const getDataToSave = () => {
    const data = {
      color,
      main,
      requiresApproval,
      hidden,
      activityAssignable: demandRelated
    };

    if (!isSystemDefined) {
      return {
        ...data,
        key,
        displayAs: displayAs || null,
        archived,
        billable,
        affectsAvailability
      };
    }

    return data;
  };

  const handleSave = () => {
    const data = getDataToSave();
    if (isNew) {
      return new Promise<void>((resolve) => {
        createBookingCategory(data)
          .then(() => {
            onSaved();
            growl({
              message: t("bookingModule:config.categories.created")
            });
            return resolve();
          })
          .catch(({ response }: AxiosError) => setErrors(getFormattedErrors(response?.data)));
      });
    }

    return new Promise<void>((resolve) => {
      updateBookingCategory(category.id, data)
        .then(() => {
          onSaved();
          growl({
            message: t("bookingModule:config.categories.updated")
          });
          resolve();
        })
        .catch(({ response }: AxiosError) => setErrors(getFormattedErrors(response?.data)));
    });
  };

  return (
    <ConfirmModal
      title={t(`bookingModule:config.categories.${isNew ? "addTitle" : "editTitle"}`)}
      handleOK={handleSave}
      handleClose={onClose}
      labelOK={t("translation:save")}
      style={{ maxWidth: 700 }}
    >
      <div className={css.container}>
        <div className={css.leftColumn}>
          <InputFieldSet
            error={errors?.key || errors?.booking_category_uniqueness}
            placeholder={t("translation:name")}
            onChange={setKey}
            value={isSystemDefined ? `${key} (${t("bookingModule:config.categories.systemDefined")})` : key}
            disabled={!isNew || isSystemDefined}
          />
          <InputFieldSet
            error={errors?.display_as || errors?.booking_category_uniqueness}
            placeholder={t("bookingModule:config.categories.displayAs")}
            onChange={setDisplayAs}
            value={
              isSystemDefined
                ? `${displayAs} (${t("bookingModule:config.categories.systemDefined")})`
                : displayAs
            }
            disabled={isSystemDefined}
          />
          <div className={css.checkboxGroup}>
            <Checkbox
              label={t("translation:billable")}
              checked={billable}
              handleChange={setBillable}
              disabled={isSystemDefined || hidden}
            />
            <Checkbox
              label={t("translation:affectsAvailability")}
              checked={affectsAvailability}
              handleChange={setAffectsAvailability}
              disabled={isSystemDefined}
            />
          </div>
          <div className={css.toggleGroup}>
            <Typography variant="labelRegular" className={css.propertiesLabel}>
              {t("bookingModule:config.categories.properties")}
            </Typography>
            <div className={css.toggle}>
              <Toggle
                reversed
                error={errors?.main}
                description={t("translation:main")}
                checked={main}
                onChange={(value) => {
                  setMain(value);
                  if (value) {
                    setDemandRelated(true);
                    setArchived(false);
                  }
                }}
                inline
                disabled={hidden}
              />
            </div>
            <div className={css.toggle}>
              <Toggle
                reversed
                error={errors?.demandRelated}
                description={t("bookingModule:config.categories.demandRelated")}
                disabled={main || hidden}
                checked={demandRelated}
                onChange={(value) => {
                  setDemandRelated(value);
                  if (!value) {
                    setMain(value);
                  }
                }}
                inline
              />
            </div>
            <div className={css.toggle}>
              <Toggle
                reversed
                error={errors?.hidden}
                description={t("bookingModule:config.categories.dontShowInTheUI")}
                checked={hidden}
                onChange={setHidden}
                inline
                disabled={main || billable || affectsAvailability || requiresApproval !== "never"}
              />
              <TooltipIcon
                content={t("bookingModule:config.categories.infoTooltipContent")}
                IconComponent={(props) => <InfoIcon className={css.toggleInfoIcon} {...props} />}
                height={20}
                width={20}
                tooltipProps={{
                  maxWidth: 250
                }}
              />
            </div>
            {!isNew && (
              <div className={css.toggle}>
                <Toggle
                  reversed
                  error={errors?.archived}
                  description={t("translation:archive")}
                  checked={archived}
                  onChange={setArchived}
                  disabled={main || isSystemDefined}
                  inline
                />
              </div>
            )}
          </div>
          <div className={css.requiresApproval}>
            <Select
              /* @ts-ignore */
              label={t("bookingModule:config.categories.requiresApproval")}
              value={requiresApprovalValue}
              controlledValue
              options={requiresApprovalOptions}
              handleChange={setRequiresApproval}
              selectedIndex={requiresApprovalIndex}
              disabled={hidden}
              kind="bordered"
              noPadding
            />
          </div>
        </div>
        <ColourPicker onClick={setColor} selected={color} />
      </div>
    </ConfirmModal>
  );
};
